import React, {
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { createContext, Dispatch, SetStateAction } from 'react';

export type DeviceControlsConfig = {
  viewMode: number;
  theme: number;
  background: string;
};

export type DeviceControlsContextType = DeviceControlsConfig & {
  setViewMode: Dispatch<SetStateAction<number>>;
  setTheme: Dispatch<SetStateAction<number>>;
  setBackground: Dispatch<SetStateAction<string>>;
};

const deviceControlContext = createContext({
  viewMode: 0,
  setViewMode: null,
  theme: 0,
  setTheme: null,
  background: 'none',
  setBackground: null
});

export const DeviceControlContextProvider = ({
  children
}: PropsWithChildren) => {
  const [viewMode, setViewMode] = useState(0);
  /** theme color: 0.0 for light, 1.0 for dark */
  const [theme, setTheme] = useState(0);
  const [background, setBackground] = useState<string>('none');

  // When the value in the social media iframe changes, update it here to synchronize the value
  useEffect(() => {
    const handleMessages = (event) => {
      if (event.data?.type === 'UPDATE_DEVICE_CONTROLS' && event.data) {
        setTheme(event.data.theme);
        setBackground(event.data.background);
        setViewMode(event.data.viewMode);
      }
    };

    window.addEventListener('message', handleMessages);
    return () => {
      window.removeEventListener('message', handleMessages);
    };
  }, []);

  const context = useMemo(() => {
    return {
      viewMode,
      setViewMode,
      theme,
      setTheme,
      background,
      setBackground
    };
  }, [viewMode, setViewMode, theme, setTheme, background, setBackground]);

  return (
    <deviceControlContext.Provider value={context}>
      {children}
    </deviceControlContext.Provider>
  );
};

export const useDeviceControls = (): DeviceControlsContextType => {
  return useContext(deviceControlContext);
};
